import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, useFieldArray, Controller, useWatch, type Control } from 'react-hook-form';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Autocomplete,
  FormControlLabel,
  Checkbox,
  Switch,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  Collapse,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  ListItemSecondaryAction,
  Tabs,
  Tab,
  Chip,
  Divider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import RepeatIcon from '@mui/icons-material/Repeat';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import StorefrontIcon from '@mui/icons-material/Storefront';
import {
  useQuote,
  useCreateQuote,
  useUpdateQuote,
  useQuoteCustomers,
  useQuoteTeams,
  useQuoteTeamCargoTypes,
  useQuoteLabels,
  useQuoteProducts,
  useTrailers,
  useQuoteTemplates,
  useCreateQuoteTemplate,
  useDeleteQuoteTemplate,
  useQuoteCatalog,
  useSalesLocations,
} from '../../hooks/useApi';
import { api } from '../../services/api';
import type { QuoteCreateInput, QuoteStop } from '../../types';
import PlacesAutocomplete, { type PlaceResult } from '../../components/common/PlacesAutocomplete';

const allCargoTypes: Record<string, string> = {
  PALLET_GOODS: 'Pallegods',
  GENERAL_CARGO: 'Stykgods',
  COOL: 'Køl',
  FREEZE: 'Frost',
  BULK_CARGO: 'Bulk',
  MACHINES: 'Maskiner',
  PACKAGES: 'Pakker',
  CONCRETE: 'Beton',
  PHARMA: 'Pharma',
  CARS: 'Biler',
  BOATS: 'Både',
  LIVE_ANIMALS: 'Levende dyr',
  CONTAINER: 'Container',
  '20_FEET': '20 fod',
  '20_FEET_HIGH_CUBE': '20 fod High Cube',
  '40_FEET': '40 fod',
  '40_FEET_HIGH_CUBE': '40 fod High Cube',
  '8_FEET': '8 fod',
  '10_FEET': '10 fod',
  SERVICE: 'Service',
  CRANE_WORK: 'Kranarbejde',
  SERVICE_VAN: 'Servicevogn',
  WORKSHOP: 'Værksted',
  WASTE: 'Affald',
  COLLECTION: 'Indsamling',
  SLUDGE: 'Slam',
  LIQUID: 'Væske',
  MISCELLANEOUS: 'Diverse',
};

// Cargo type select that filters options to those available on the selected team
// (derived from team trailers' qualities). Falls back to all types if no team
// selected or team has no trailers with cargo-type qualities.
function CargoTypeSelect({ control, loadIdx }: { control: Control<QuoteFormData>; loadIdx: number }) {
  const teamId = useWatch({ control, name: `loads.${loadIdx}.teamId` }) as string | undefined;
  const currentValue = useWatch({ control, name: `loads.${loadIdx}.cargoType` }) as string | undefined;
  const { data: teamTypes } = useQuoteTeamCargoTypes(teamId || null);

  const allKeys = Object.keys(allCargoTypes);
  const filteredKeys =
    teamId && teamTypes && teamTypes.length > 0
      ? allKeys.filter(k => teamTypes.includes(k))
      : allKeys;

  // If the current value is set but not in the filtered list (e.g. team changed
  // after a value was picked), keep it visible so the user can see and adjust.
  const visibleKeys = currentValue && !filteredKeys.includes(currentValue)
    ? [...filteredKeys, currentValue]
    : filteredKeys;

  return (
    <Controller
      name={`loads.${loadIdx}.cargoType`}
      control={control}
      render={({ field }) => (
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <Select {...field} displayEmpty>
            <MenuItem value=""><em>Godstype</em></MenuItem>
            {visibleKeys.map(key => (
              <MenuItem key={key} value={key}>
                {allCargoTypes[key] || key}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    />
  );
}

const recurrencePatterns = [
  { value: 'WEEKLY', label: 'Ugentligt' },
  { value: 'BIWEEKLY', label: 'Hver 2. uge' },
  { value: 'EVERY_X_DAYS', label: 'Hver X dage' },
  { value: 'SPECIFIC_WEEKDAYS', label: 'Bestemte ugedage' },
  { value: 'DAILY', label: 'Dagligt' },
];

const weekdayOptions = [
  { value: 1, label: 'Man' }, { value: 2, label: 'Tir' }, { value: 3, label: 'Ons' },
  { value: 4, label: 'Tor' }, { value: 5, label: 'Fre' }, { value: 6, label: 'Lør' }, { value: 7, label: 'Søn' },
];

interface StopFormData {
  date: string;
  time: string;
  loadmeters: string;
  weight: string;
  ref: string;
  contact: string;
  contactPhone: string;
  placeName: string;
  address: string;
  postcode: string;
  city: string;
  country: string;
  salesLocationId: string; // '' = fri adresse; ellers reference til en SalesLocation
}

interface PriceLineFormData {
  description: string;
  amount: number;
  quantity: number;
  unitName: string;
  productId: string;
  productNumber: string;
  discountPercentage: string;
  loadId: string; // '' = hele tilbuddet
}

// Per-load (each load → its own DORA cargo when converted)
interface LoadFormData {
  id: string;
  cargoType: string;
  description: string;
  description2: string;
  note: string;
  adr: boolean;
  adrText: string;
  minTemperature: string;
  maxTemperature: string;
  teamId: string;
  labelId: string;
  pickups: StopFormData[];
  dropoffs: StopFormData[];
}

interface QuoteFormData {
  isRecurring: boolean;
  recurringPattern: string;
  recurringWeekdays: number[];
  recurringIntervalDays: string;
  recurringEndDate: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  customerNumber: string;
  loads: LoadFormData[];
  priceLines: PriceLineFormData[];
  currency: string;
  validUntil: string;
  terms: string;
  // Internal
  internalNote: string;
  // DG / margin estimation fields
  estimateTrailerId: string;
  estimateKm: string;
  estimateHours: string;
  estimateCalendarDays: string;
  // Customer-facing view controls
  customerMessage: string;
  hidePickupsFromCustomer: boolean;
  hideDropoffsFromCustomer: boolean;
  hideCustomerNumberOnPdf: boolean;
  // Internal reminder to creator
  reminderEnabled: boolean;
  reminderHour1: string;
  reminderHour2: string;
  reminderHour3: string;
}

const emptyStop: StopFormData = {
  date: '', time: '', loadmeters: '', weight: '', ref: '', contact: '', contactPhone: '',
  placeName: '', address: '', postcode: '', city: '', country: 'DK',
  salesLocationId: '',
};

// Normalizes a search string so Danish characters and their ASCII equivalents match.
// E.g. "Møller" → "moller", "Aarhus" and "Århus" both → "arhus", "straße" → "strasse".
function normalizeSearch(s: string): string {
  return (s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')  // strip combining diacritics
    .replace(/æ/g, 'ae')
    .replace(/ø/g, 'o')
    .replace(/å/g, 'a')
    .replace(/ß/g, 'ss')
    .replace(/aa/g, 'a')               // "aarhus" ≡ "århus" ≡ "arhus"
    .replace(/oe/g, 'o')               // "moeller" ≡ "møller"
    .trim();
}

function makeEmptyLoad(): LoadFormData {
  return {
    id: crypto.randomUUID(),
    cargoType: 'MISCELLANEOUS', description: '', description2: '', note: '',
    adr: false, adrText: '', minTemperature: '', maxTemperature: '',
    teamId: '', labelId: '',
    pickups: [{ ...emptyStop }],
    dropoffs: [{ ...emptyStop }],
  };
}

const emptyPriceLine: PriceLineFormData = {
  description: '', amount: 0, quantity: 1, unitName: 'stk',
  productId: '', productNumber: '', discountPercentage: '',
  loadId: '',
};

// Default "Gyldig til" = 30 dage frem fra i dag, formatteret som YYYY-MM-DD
function defaultValidUntil(): string {
  const d = new Date();
  d.setDate(d.getDate() + 30);
  return d.toISOString().slice(0, 10);
}

function toFormStop(stop: QuoteStop): StopFormData {
  return {
    date: stop.date || '', time: stop.time || '', loadmeters: stop.loadmeters || '',
    weight: stop.weight || '', ref: stop.ref || '', contact: stop.contact || '',
    contactPhone: stop.contactPhone || '', placeName: stop.place?.placeName || '',
    address: stop.place?.address || '', postcode: stop.place?.postcode || '',
    city: stop.place?.city || '', country: stop.place?.country || 'DK',
    salesLocationId: (stop as { salesLocationId?: string }).salesLocationId || '',
  };
}

function fromFormStop(s: StopFormData): QuoteStop & { salesLocationId?: string | null } {
  return {
    date: s.date, time: s.time || null, loadmeters: s.loadmeters || undefined,
    weight: s.weight || undefined, ref: s.ref || null, contact: s.contact || null,
    contactPhone: s.contactPhone || null,
    place: { placeName: s.placeName || null, address: s.address || '', postcode: s.postcode || '', city: s.city || null, country: s.country || 'DK' },
    salesLocationId: s.salesLocationId || null,
  };
}

// Tiny uppercase section label
function SectionLabel({ children, sx }: { children: React.ReactNode; sx?: any }) {
  return (
    <Typography
      variant="caption"
      color="text.secondary"
      fontWeight={700}
      sx={{
        display: 'block',
        mb: 0.5,
        fontSize: 10,
        textTransform: 'uppercase',
        letterSpacing: 0.6,
        ...sx,
      }}
    >
      {children}
    </Typography>
  );
}

// Compact single-row stop with Google Places search.
// `prefix` is the full path to this stop in the form (e.g. `loads.0.pickups.2`).
function StopRow({
  prefix, index, register, onRemove, canRemove, kind, setValue: setFormValue, watch, salesLocations,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  prefix: any;
  index: number;
  register: ReturnType<typeof useForm<QuoteFormData>>['register'];
  onRemove: () => void;
  canRemove: boolean;
  kind: 'pickup' | 'dropoff';
  setValue: ReturnType<typeof useForm<QuoteFormData>>['setValue'];
  watch: ReturnType<typeof useForm<QuoteFormData>>['watch'];
  salesLocations: import('../../types').SalesLocation[];
}) {
  const [expanded, setExpanded] = useState(false);
  const dotColor = kind === 'pickup' ? 'success.main' : 'error.main';

  // react-hook-form types cannot express arbitrary nested paths — cast once to keep callsites clean.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const regAny = register as any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const setValAny = setFormValue as any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const watchAny = watch as any;

  const currentLocId = watchAny(`${prefix}.salesLocationId`) || '';
  const currentLoc = salesLocations.find(l => l.id === currentLocId) || null;

  const handlePlaceSelect = (place: PlaceResult | null) => {
    if (!place) return;
    setValAny(`${prefix}.address`, place.address);
    setValAny(`${prefix}.postcode`, place.postcode);
    setValAny(`${prefix}.city`, place.city);
    setValAny(`${prefix}.country`, place.country || 'DK');
    if (place.formatted) setValAny(`${prefix}.placeName`, place.formatted.split(',')[0] || '');
    // Manuelt valgt adresse → fjern eventuel lokations-tilknytning
    setValAny(`${prefix}.salesLocationId`, '');
  };

  const handleLocationSelect = (loc: import('../../types').SalesLocation | null) => {
    if (!loc) {
      setValAny(`${prefix}.salesLocationId`, '');
      return;
    }
    setValAny(`${prefix}.salesLocationId`, loc.id);
    setValAny(`${prefix}.placeName`, loc.placeName || loc.name);
    setValAny(`${prefix}.address`, loc.address || '');
    setValAny(`${prefix}.postcode`, loc.postcode || '');
    setValAny(`${prefix}.city`, loc.city || '');
    setValAny(`${prefix}.country`, loc.country || 'DK');
  };

  return (
    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mb: 0.5, flexWrap: 'wrap' }}>
      <Box sx={{
        width: 18, height: 18, borderRadius: '50%', bgcolor: dotColor, color: 'white',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 10, fontWeight: 700, flexShrink: 0,
      }}>
        {index + 1}
      </Box>
      <Box sx={{ flex: 2, minWidth: 110 }}>
        {currentLoc ? (
          <Chip
            icon={<StorefrontIcon sx={{ fontSize: 14 }} />}
            label={currentLoc.name}
            onDelete={() => handleLocationSelect(null)}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ width: '100%', justifyContent: 'space-between' }}
          />
        ) : (
          <PlacesAutocomplete
            label=""
            placeholder="Søg adresse..."
            onChange={handlePlaceSelect}
          />
        )}
      </Box>
      {kind === 'pickup' && salesLocations.length > 0 && !currentLoc && (
        <Autocomplete<import('../../types').SalesLocation>
          size="small"
          sx={{ width: 160 }}
          options={salesLocations.filter(l => l.active)}
          getOptionLabel={(opt) => opt.name}
          value={null}
          onChange={(_, val) => handleLocationSelect(val)}
          renderInput={(params) => <TextField {...params} placeholder="Salgssted..." />}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <Box>
                <Typography variant="body2" fontWeight={500}>{option.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {[option.postcode, option.city].filter(Boolean).join(' ')}
                </Typography>
              </Box>
            </li>
          )}
        />
      )}
      <TextField size="small" placeholder="Postnr" sx={{ width: 65 }} {...regAny(`${prefix}.postcode`)} />
      <TextField size="small" placeholder="By" sx={{ flex: 1, minWidth: 70 }} {...regAny(`${prefix}.city`)} />
      <Tooltip title="Lad feltet stå tomt hvis datoen er ukendt. Ved konvertering til DORA-ordre skal den udfyldes.">
        <TextField size="small" type="date" sx={{ width: 130 }}
          InputLabelProps={{ shrink: true }}
          {...regAny(`${prefix}.date`)} />
      </Tooltip>
      <TextField size="small" type="time" sx={{ width: 95 }} {...regAny(`${prefix}.time`)} />
      <IconButton size="small" onClick={() => setExpanded(!expanded)} sx={{ p: 0.25 }}>
        {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
      </IconButton>
      {canRemove && (
        <IconButton size="small" color="error" onClick={onRemove} sx={{ p: 0.25 }}><DeleteIcon fontSize="small" /></IconButton>
      )}
      <Collapse in={expanded} sx={{ width: '100%' }}>
        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, ml: 2.5, flexWrap: 'wrap' }}>
          <TextField size="small" placeholder="Land" sx={{ width: 55 }} {...regAny(`${prefix}.country`)} />
          <TextField size="small" placeholder="Stedsnavn" sx={{ flex: 1, minWidth: 100 }} {...regAny(`${prefix}.placeName`)} />
          <TextField size="small" placeholder="Adresse (manuelt)" sx={{ flex: 1, minWidth: 100 }} {...regAny(`${prefix}.address`)} />
          <TextField size="small" placeholder="LDM" sx={{ width: 60 }} {...regAny(`${prefix}.loadmeters`)} />
          <TextField size="small" placeholder="Vægt (kg)" sx={{ width: 80 }} {...regAny(`${prefix}.weight`)} />
          <TextField size="small" placeholder="Ref" sx={{ flex: 1, minWidth: 70 }} {...regAny(`${prefix}.ref`)} />
          <TextField size="small" placeholder="Kontakt" sx={{ flex: 1, minWidth: 90 }} {...regAny(`${prefix}.contact`)} />
        </Box>
      </Collapse>
    </Box>
  );
}

export default function QuoteForm() {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const navigate = useNavigate();

  const { data: existingQuote, isLoading: loadingQuote } = useQuote(id || '');
  const createQuote = useCreateQuote();
  const updateQuote = useUpdateQuote();
  const { data: customers = [] } = useQuoteCustomers();
  const { data: teams = [] } = useQuoteTeams();
  const { data: labels = [] } = useQuoteLabels();
  const { data: productsData } = useQuoteProducts();
  const products = productsData?.products || [];
  const { data: trailers = [] } = useTrailers();

  const [marginEstimate, setMarginEstimate] = useState<{
    totalCost: number;
    dg: number;
    dgPct: number;
    kilometers: number;
    hours: number;
    source: 'auto' | 'manual' | 'mixed';
  } | null>(null);
  const [marginLoading, setMarginLoading] = useState(false);

  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [catalogDialogOpen, setCatalogDialogOpen] = useState(false);
  const [saveTemplateDialog, setSaveTemplateDialog] = useState<{ open: boolean; name: string; bindToCustomer: boolean }>({
    open: false, name: '', bindToCustomer: true,
  });
  const createTemplate = useCreateQuoteTemplate();
  const deleteTemplate = useDeleteQuoteTemplate();
  const { data: catalog = [] } = useQuoteCatalog();
  const { data: salesLocations = [] } = useSalesLocations();

  // Find auto-trigger for et valgt produkt baseret på load's pickups' salgssteder.
  // Hvis loadId er tom, tjek alle læs. Returnerer første match eller null.
  function findAutoTriggerForProduct(doraProductId: string, loadId: string | null) {
    const loadsData = watch('loads') || [];
    const loadsToCheck = loadId ? loadsData.filter(l => l.id === loadId) : loadsData;
    for (const load of loadsToCheck) {
      for (const pickup of load.pickups) {
        const locId = pickup.salesLocationId;
        if (!locId) continue;
        const loc = salesLocations.find(l => l.id === locId);
        if (!loc) continue;
        const locProd = loc.products.find(p => p.doraProductId === doraProductId && p.autoTriggerDoraProductId && p.active);
        if (locProd) {
          return {
            locationName: loc.name,
            triggerProductId: locProd.autoTriggerDoraProductId!,
            triggerProductNumber: locProd.autoTriggerProductNumber || '',
            triggerProductName: locProd.autoTriggerProductName || '',
            factor: locProd.autoTriggerFactor ?? 1,
            price: locProd.autoTriggerPrice ?? 0,
            unitName: locProd.autoTriggerUnitName || 'stk',
            sourceLoadId: load.id,
          };
        }
      }
    }
    return null;
  }

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });

  const { register, handleSubmit, control, reset, watch, setValue, formState: { errors } } = useForm<QuoteFormData>({
    defaultValues: {
      isRecurring: false,
      recurringPattern: 'WEEKLY', recurringWeekdays: [], recurringIntervalDays: '7', recurringEndDate: '',
      customerName: '', customerEmail: '', customerPhone: '', customerNumber: '',
      loads: [makeEmptyLoad()],
      priceLines: [{ ...emptyPriceLine }], currency: 'DKK',
      validUntil: defaultValidUntil(), terms: '',
      internalNote: '',
      estimateTrailerId: '', estimateKm: '', estimateHours: '', estimateCalendarDays: '1',
      customerMessage: '',
      hidePickupsFromCustomer: false,
      hideDropoffsFromCustomer: false,
      hideCustomerNumberOnPdf: false,
      reminderEnabled: true,
      reminderHour1: '1',
      reminderHour2: '72',
      reminderHour3: '168',
    },
  });

  // Top-level tab: 0 = Intern, 1 = Tilbud til kunde
  const [activeTab, setActiveTab] = useState<0 | 1>(0);

  const loadArray = useFieldArray({ control, name: 'loads' });
  const priceLineArray = useFieldArray({ control, name: 'priceLines' });
  const watchPriceLines = watch('priceLines');
  const totalAmount = watchPriceLines.reduce((sum, line) => sum + (line.amount || 0) * (line.quantity || 1), 0);
  const watchCustomerNumber = watch('customerNumber');
  const { data: templates = [] } = useQuoteTemplates(watchCustomerNumber || undefined);
  const watchTrailerId = watch('estimateTrailerId');
  const watchKm = watch('estimateKm');
  const watchHours = watch('estimateHours');
  const watchCalendarDays = watch('estimateCalendarDays');
  const isPending = createQuote.isPending || updateQuote.isPending;

  // --- Auto-save ---
  // First input triggers creation; subsequent edits update the draft silently.
  // Avoids losing work if user navigates away without explicitly saving.
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const autoSavedIdRef = useRef<string | null>(null);           // id when we auto-created the quote
  const lastSavedPayloadRef = useRef<string>('');               // JSON of last saved payload (dedup)

  // Debounced live margin estimation
  useEffect(() => {
    if (totalAmount <= 0) {
      setMarginEstimate(null);
      return;
    }
    const timer = setTimeout(async () => {
      setMarginLoading(true);
      try {
        const km = watchKm ? parseFloat(watchKm) : null;
        const hours = watchHours ? parseFloat(watchHours) : null;
        const calendarDays = watchCalendarDays ? parseInt(watchCalendarDays) : 1;
        const result = await api.estimateQuoteMargin({
          trailerId: watchTrailerId || null,
          distanceKm: km,
          hours,
          calendarDays,
          totalRevenue: totalAmount,
          invoiceLineCount: watchPriceLines.filter(l => l.description).length,
        });
        setMarginEstimate({
          totalCost: result.totalCost,
          dg: result.dg,
          dgPct: result.dgPct,
          kilometers: result.kilometers,
          hours: result.hours,
          source: result.source,
        });
      } catch (err) {
        console.warn('Margin estimate failed:', err);
        setMarginEstimate(null);
      } finally {
        setMarginLoading(false);
      }
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalAmount, watchTrailerId, watchKm, watchHours, watchCalendarDays]);

  function dgColor(pct: number): 'success' | 'warning' | 'error' {
    if (pct < 15) return 'error';
    if (pct < 25) return 'warning';
    return 'success';
  }

  // --- Auto-save effect ---
  // Watches the entire form, debounces 1500ms, then persists.
  // Gate: need at least customerName + valid email.
  const watchedAll = watch();
  useEffect(() => {
    // Finaliserede tilbud (ACCEPTED/CONVERTED/REJECTED/EXPIRED) kan ikke redigeres
    if (existingQuote && !['DRAFT', 'SENT', 'ACCEPTED'].includes(existingQuote.status)) return;

    const name = watchedAll.customerName?.trim();
    const email = watchedAll.customerEmail?.trim();
    if (!name || !email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;

    const timer = setTimeout(async () => {
      try {
        const payload = buildPayload(watchedAll);
        const payloadKey = JSON.stringify(payload);
        if (payloadKey === lastSavedPayloadRef.current) return;

        setAutoSaveStatus('saving');
        const existingId = id || autoSavedIdRef.current;
        if (!existingId) {
          // Kald api direkte (ikke via mutation hook) — ellers invalideres ['quote']
          // og existingQuote refetches, hvilket rammer reset-useEffect'et og wiper
          // brugerens igangværende input.
          const result = await api.createQuote(payload);
          autoSavedIdRef.current = result.id;
          // Opdatér URL UDEN at trigge React Router så komponenten ikke remountes
          // og formularens state bevares intakt.
          window.history.replaceState(null, '', `/tilbud/${result.id}/rediger`);
        } else {
          await api.updateQuote(existingId, payload);
        }
        lastSavedPayloadRef.current = payloadKey;
        setAutoSaveStatus('saved');
        setLastSavedAt(new Date());
      } catch {
        setAutoSaveStatus('error');
      }
    }, 1500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedAll]);

  // Apply a template to the form. Templates are single-cargo, so they populate loads[0].
  function applyTemplate(t: typeof templates[number]) {
    const pickups: StopFormData[] = (t.pickups || []).map((p: any) => ({
      date: p.date || '', time: p.time || '', loadmeters: p.loadmeters || '',
      weight: p.weight || '', ref: p.ref || '', contact: p.contact || '',
      contactPhone: p.contactPhone || '',
      placeName: p.place?.placeName || '', address: p.place?.address || '',
      postcode: p.place?.postcode || '', city: p.place?.city || '',
      country: p.place?.country || 'DK',
      salesLocationId: '',
    }));
    const dropoffs: StopFormData[] = (t.dropoffs || []).map((d: any) => ({
      date: d.date || '', time: d.time || '', loadmeters: d.loadmeters || '',
      weight: d.weight || '', ref: d.ref || '', contact: d.contact || '',
      contactPhone: d.contactPhone || '',
      placeName: d.place?.placeName || '', address: d.place?.address || '',
      postcode: d.place?.postcode || '', city: d.place?.city || '',
      country: d.place?.country || 'DK',
      salesLocationId: '',
    }));
    setValue('loads', [{
      id: crypto.randomUUID(),
      cargoType: t.cargoType || '',
      description: t.description || '',
      description2: t.description2 || '',
      note: t.note || '',
      adr: t.adr,
      adrText: t.adrText || '',
      teamId: t.teamId || '',
      labelId: t.labelId || '',
      minTemperature: '',
      maxTemperature: '',
      pickups: pickups.length ? pickups : [{ ...emptyStop }],
      dropoffs: dropoffs.length ? dropoffs : [{ ...emptyStop }],
    }]);
    if (t.priceLines?.length) setValue('priceLines', t.priceLines.map((l: any) => ({
      description: l.description || '',
      amount: l.amount || 0,
      quantity: l.quantity || 1,
      unitName: l.unitName || 'stk',
      productId: l.productId || '',
      productNumber: l.productNumber || '',
      discountPercentage: l.discountPercentage || '',
      loadId: '',
    })));
    setTemplateDialogOpen(false);
    setSnackbar({ open: true, message: `Skabelon "${t.name}" indlæst`, severity: 'success' });
  }

  function openSaveTemplateDialog() {
    const data = watch();
    const firstLoad = data.loads?.[0];
    const suggested = `${data.customerName || ''} — ${allCargoTypes[firstLoad?.cargoType || ''] || firstLoad?.cargoType || 'Tilbud'}`.trim().replace(/^—\s*/, '');
    setSaveTemplateDialog({ open: true, name: suggested, bindToCustomer: !!data.customerNumber });
  }

  async function confirmSaveTemplate() {
    const data = watch();
    const name = saveTemplateDialog.name.trim();
    if (!name) {
      setSnackbar({ open: true, message: 'Indtast et navn', severity: 'error' });
      return;
    }
    // Templates are single-cargo — snapshot the first load
    const firstLoad = data.loads?.[0];
    if (!firstLoad) {
      setSnackbar({ open: true, message: 'Intet læs at gemme', severity: 'error' });
      return;
    }
    try {
      await createTemplate.mutateAsync({
        name,
        customerNumber: saveTemplateDialog.bindToCustomer ? (data.customerNumber || null) : null,
        quoteType: data.isRecurring ? 'RECURRING' : 'TRANSPORT',
        cargoType: firstLoad.cargoType || null,
        description: firstLoad.description || null,
        description2: firstLoad.description2 || null,
        note: firstLoad.note || null,
        adr: firstLoad.adr,
        adrText: firstLoad.adrText || null,
        teamId: firstLoad.teamId || null,
        labelId: firstLoad.labelId || null,
        requirements: [],
        qualities: [],
        pickups: firstLoad.pickups.map(fromFormStop),
        dropoffs: firstLoad.dropoffs.map(fromFormStop),
        priceLines: data.priceLines.filter(l => l.description).map(l => ({
          description: l.description,
          amount: l.amount,
          quantity: l.quantity,
          unitName: l.unitName || null,
          productId: l.productId || null,
          productNumber: l.productNumber || null,
          discountPercentage: l.discountPercentage || null,
        })),
      });
      setSaveTemplateDialog({ open: false, name: '', bindToCustomer: true });
      setSnackbar({ open: true, message: 'Skabelon gemt', severity: 'success' });
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.response?.data?.error || 'Kunne ikke gemme skabelon', severity: 'error' });
    }
  }

  useEffect(() => {
    if (isEdit && existingQuote) {
      const q = existingQuote;
      // Server normaliserer altid til loads-array; fallback hvis for sikkerhed
      const loadsFromQuote: LoadFormData[] = (q.loads && q.loads.length > 0 ? q.loads : [{
        id: undefined,
        cargoType: q.cargoType,
        description: q.description,
        description2: q.description2,
        note: q.note,
        adr: q.adr,
        adrText: q.adrText,
        minTemperature: q.minTemperature,
        maxTemperature: q.maxTemperature,
        teamId: q.teamId,
        labelId: q.labelId,
        requirements: q.requirements || [],
        qualities: q.qualities || [],
        pickups: q.pickups,
        dropoffs: q.dropoffs,
      }]).map(l => ({
        id: l.id || crypto.randomUUID(),
        cargoType: l.cargoType || '',
        description: l.description || '',
        description2: l.description2 || '',
        note: l.note || '',
        adr: l.adr || false,
        adrText: l.adrText || '',
        minTemperature: l.minTemperature != null ? String(l.minTemperature) : '',
        maxTemperature: l.maxTemperature != null ? String(l.maxTemperature) : '',
        teamId: l.teamId || '',
        labelId: l.labelId || '',
        pickups: (l.pickups || []).map(toFormStop),
        dropoffs: (l.dropoffs || []).map(toFormStop),
      }));

      reset({
        isRecurring: q.quoteType === 'RECURRING',
        recurringPattern: q.recurringPattern || 'WEEKLY',
        recurringWeekdays: q.recurringWeekdays || [],
        recurringIntervalDays: q.recurringIntervalDays != null ? String(q.recurringIntervalDays) : '7',
        recurringEndDate: q.recurringEndDate ? q.recurringEndDate.split('T')[0] : '',
        customerName: q.customerName, customerEmail: q.customerEmail,
        customerPhone: q.customerPhone || '', customerNumber: q.customerNumber || '',
        loads: loadsFromQuote,
        priceLines: q.priceLines.length > 0
          ? q.priceLines.map(p => ({
              description: p.description,
              amount: p.amount,
              quantity: p.quantity,
              unitName: p.unitName || 'stk',
              productId: p.productId || '',
              productNumber: p.productNumber || '',
              discountPercentage: p.discountPercentage || '',
              loadId: (p as { loadId?: string | null }).loadId || '',
            }))
          : [{ ...emptyPriceLine }],
        currency: q.currency || 'DKK',
        validUntil: q.validUntil ? q.validUntil.split('T')[0] : '',
        terms: q.terms || '',
        internalNote: q.internalNote || '',
        estimateTrailerId: '', estimateKm: '', estimateHours: '', estimateCalendarDays: '1',
        customerMessage: q.customerMessage || '',
        hidePickupsFromCustomer: q.hidePickupsFromCustomer ?? false,
        hideDropoffsFromCustomer: q.hideDropoffsFromCustomer ?? false,
        hideCustomerNumberOnPdf: q.hideCustomerNumberOnPdf ?? false,
        reminderEnabled: q.reminderEnabled ?? true,
        reminderHour1: String(q.reminderIntervalsHours?.[0] ?? 1),
        reminderHour2: String(q.reminderIntervalsHours?.[1] ?? 72),
        reminderHour3: String(q.reminderIntervalsHours?.[2] ?? 168),
      });
      if (loadsFromQuote.some(l => l.adr || l.minTemperature || l.maxTemperature || l.note || l.description2)) {
        setShowAdvanced(true);
      }
    }
  }, [isEdit, existingQuote, reset]);

  const buildPayload = (data: QuoteFormData): QuoteCreateInput => {
    const loads = data.loads.map(l => ({
      id: l.id,
      cargoType: l.cargoType || null,
      description: l.description || null,
      description2: l.description2 || null,
      note: l.note || null,
      adr: l.adr,
      adrText: l.adrText || null,
      minTemperature: l.minTemperature ? parseFloat(l.minTemperature) : null,
      maxTemperature: l.maxTemperature ? parseFloat(l.maxTemperature) : null,
      teamId: l.teamId || null,
      labelId: l.labelId || null,
      requirements: [],
      qualities: [],
      pickups: l.pickups.map(fromFormStop),
      dropoffs: l.dropoffs.map(fromFormStop),
    }));
    const firstLoad = loads[0];
    return {
      quoteType: data.isRecurring ? 'RECURRING' : 'TRANSPORT',
      recurringPattern: data.isRecurring ? (data.recurringPattern || null) : null,
      recurringWeekdays: data.isRecurring ? data.recurringWeekdays : [],
      recurringIntervalDays: data.isRecurring && data.recurringPattern === 'EVERY_X_DAYS' && data.recurringIntervalDays
        ? parseInt(data.recurringIntervalDays) : null,
      recurringEndDate: data.isRecurring ? (data.recurringEndDate || null) : null,
      customerName: data.customerName, customerEmail: data.customerEmail,
      customerPhone: data.customerPhone || null, customerNumber: data.customerNumber || null,
      // Legacy top-level fields — mirror first load for bagudkompatibilitet (server gør det samme)
      cargoType: firstLoad.cargoType,
      description: firstLoad.description,
      description2: firstLoad.description2,
      note: firstLoad.note,
      adr: firstLoad.adr,
      adrText: firstLoad.adrText,
      minTemperature: firstLoad.minTemperature,
      maxTemperature: firstLoad.maxTemperature,
      teamId: firstLoad.teamId,
      labelId: firstLoad.labelId,
      pickups: firstLoad.pickups,
      dropoffs: firstLoad.dropoffs,
      loads,
      priceLines: data.priceLines.filter(l => l.description).map(l => ({
        description: l.description,
        amount: l.amount,
        quantity: l.quantity,
        unitName: l.unitName || null,
        productId: l.productId || null,
        productNumber: l.productNumber || null,
        discountPercentage: l.discountPercentage || null,
        loadId: l.loadId || null,
      })),
      totalAmount, currency: data.currency || 'DKK',
      validUntil: data.validUntil || null, terms: data.terms || null,
      internalNote: data.internalNote || null,
      customerMessage: data.customerMessage || null,
      hidePickupsFromCustomer: data.hidePickupsFromCustomer,
      hideDropoffsFromCustomer: data.hideDropoffsFromCustomer,
      hideCustomerNumberOnPdf: data.hideCustomerNumberOnPdf,
      reminderEnabled: data.reminderEnabled,
      reminderIntervalsHours: [data.reminderHour1, data.reminderHour2, data.reminderHour3]
        .map(s => parseInt(s, 10))
        .filter(n => Number.isFinite(n) && n > 0),
    };
  };

  // Resolve the "effective" quote id: URL param OR auto-saved draft id from this session
  const effectiveId = () => id || autoSavedIdRef.current;

  const onSave = async (data: QuoteFormData) => {
    try {
      const payload = buildPayload(data);
      const existingId = effectiveId();
      if (existingId) {
        await updateQuote.mutateAsync({ id: existingId, data: payload });
        navigate(`/tilbud/${existingId}`);
      } else {
        const result = await createQuote.mutateAsync(payload);
        navigate(`/tilbud/${result.id}`);
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Kunne ikke gemme tilbud';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    }
  };

  if (isEdit && loadingQuote) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ pb: 10 /* space for sticky footer */ }}>
      {/* Compact header: back + title + type selector + secondary actions */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(effectiveId() ? `/tilbud/${effectiveId()}` : '/tilbud')} size="small">
            Tilbage
          </Button>
          <Typography variant="h5" fontWeight={600}>
            {isEdit ? `Rediger #${existingQuote?.quoteNumber}` : 'Nyt tilbud'}
          </Typography>
          {/* Auto-save status */}
          <Typography variant="caption" color={
            autoSaveStatus === 'error' ? 'error.main'
            : autoSaveStatus === 'saving' ? 'text.secondary'
            : autoSaveStatus === 'saved' ? 'success.main'
            : 'text.disabled'
          } sx={{ fontStyle: autoSaveStatus === 'idle' ? 'italic' : 'normal' }}>
            {autoSaveStatus === 'saving' && 'Gemmer kladde…'}
            {autoSaveStatus === 'saved' && lastSavedAt && `✓ Kladde gemt kl ${lastSavedAt.toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' })}`}
            {autoSaveStatus === 'error' && '⚠ Kunne ikke gemme automatisk'}
            {autoSaveStatus === 'idle' && !isEdit && 'Kladde oprettes automatisk når kunde + email er udfyldt'}
          </Typography>
          <Controller name="isRecurring" control={control} render={({ field }) => (
            <FormControlLabel
              sx={{ m: 0 }}
              control={
                <Switch size="small" checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <RepeatIcon sx={{ fontSize: 14 }} />
                  <Typography variant="caption" fontWeight={600}>Tilbagevendende</Typography>
                </Box>
              }
            />
          )} />
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title={templates.length === 0 ? 'Ingen skabeloner endnu' : `${templates.length} skabelon${templates.length !== 1 ? 'er' : ''} tilgængelige`}>
            <span>
              <Button startIcon={<BookmarkIcon />} size="small" onClick={() => setTemplateDialogOpen(true)} disabled={templates.length === 0}>
                Skabeloner {templates.length > 0 && `(${templates.length})`}
              </Button>
            </span>
          </Tooltip>
          <Button startIcon={<BookmarkAddIcon />} size="small" onClick={openSaveTemplateDialog}>
            Gem skabelon
          </Button>
        </Box>
      </Box>

      <Dialog open={catalogDialogOpen} onClose={() => setCatalogDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Vælg fra katalog</DialogTitle>
        <DialogContent dividers>
          {catalog.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Ingen produkter med kategori. Gå til indstillinger → tilbudskatalog for at tagge produkter.
            </Typography>
          ) : (
            (() => {
              const filtered = catalog.filter(c => c.category);
              const grouped: Record<string, typeof filtered> = {};
              for (const item of filtered) {
                const cat = item.category || 'MISC';
                if (!grouped[cat]) grouped[cat] = [];
                grouped[cat].push(item);
              }
              if (Object.keys(grouped).length === 0) {
                return (
                  <Typography variant="body2" color="text.secondary">
                    Ingen kategoriserede produkter. Tilføj kategorier i indstillinger → tilbudskatalog.
                  </Typography>
                );
              }
              return Object.entries(grouped).map(([cat, items]) => (
                <Box key={cat} sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>{cat}</Typography>
                  <List dense>
                    {items.map(item => (
                      <ListItem key={item.id} disablePadding>
                        <ListItemButton onClick={() => {
                          priceLineArray.append({
                            description: item.name,
                            amount: item.salesPrice ? parseFloat(item.salesPrice) : 0,
                            quantity: item.defaultQuantity || 1,
                            unitName: 'stk',
                            productId: item.id,
                            productNumber: item.productNumber,
                            discountPercentage: '',
                            loadId: '',
                          });
                          setCatalogDialogOpen(false);
                          setSnackbar({ open: true, message: `Tilføjet: ${item.name}`, severity: 'success' });
                        }}>
                          <ListItemText
                            primary={`${item.productNumber} — ${item.name}`}
                            secondary={item.salesPrice ? new Intl.NumberFormat('da-DK', { style: 'currency', currency: 'DKK' }).format(parseFloat(item.salesPrice)) : 'Ingen pris'}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              ));
            })()
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCatalogDialogOpen(false)}>Luk</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={templateDialogOpen} onClose={() => setTemplateDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Vælg skabelon</DialogTitle>
        <DialogContent dividers>
          {templates.length === 0 ? (
            <Typography variant="body2" color="text.secondary">Ingen skabeloner endnu.</Typography>
          ) : (
            <List dense>
              {templates.map(t => (
                <ListItem key={t.id} disablePadding>
                  <ListItemButton onClick={() => applyTemplate(t)}>
                    <ListItemText
                      primary={t.name}
                      secondary={[t.cargoType, t.customerNumber ? `Kunde: ${t.customerNumber}` : 'Generisk'].filter(Boolean).join(' · ')}
                    />
                  </ListItemButton>
                  <ListItemSecondaryAction>
                    <IconButton size="small" color="error" onClick={async (e) => {
                      e.stopPropagation();
                      if (confirm(`Slet skabelon "${t.name}"?`)) {
                        await deleteTemplate.mutateAsync(t.id);
                      }
                    }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplateDialogOpen(false)}>Luk</Button>
        </DialogActions>
      </Dialog>

      <form>
        {/* Tabs: Intern vs Tilbud til kunde */}
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v as 0 | 1)}
          sx={{ mb: 1.5, borderBottom: 1, borderColor: 'divider', minHeight: 36 }}
        >
          <Tab label="Intern" sx={{ minHeight: 36, py: 0.5 }} />
          <Tab label="Tilbud til kunde" sx={{ minHeight: 36, py: 0.5 }} />
        </Tabs>

        {/* --- INTERN TAB --- */}
        <Box sx={{ display: activeTab === 0 ? 'block' : 'none' }}>
        {/* Single-sheet form: one Card wrapping everything, sections separated by thin dividers */}
        <Card sx={{ mb: 1.5 }}>
          <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>

            {/* Recurring strip — only when isRecurring */}
            {watch('isRecurring') && (
              <Box sx={{
                px: 1.25, py: 0.75,
                bgcolor: 'primary.50', borderBottom: '1px solid', borderColor: 'primary.100',
                display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center',
              }}>
                <RepeatIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                <Typography variant="caption" fontWeight={600} color="primary.dark" sx={{ mr: 0.5 }}>Gentagelse:</Typography>
                <Controller name="recurringPattern" control={control} render={({ field }) => (
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <Select {...field} displayEmpty>
                      {recurrencePatterns.map(p => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
                    </Select>
                  </FormControl>
                )} />
                {watch('recurringPattern') === 'EVERY_X_DAYS' && (
                  <TextField size="small" placeholder="Hver X dage" type="number" sx={{ width: 100 }}
                    {...register('recurringIntervalDays')} />
                )}
                {['WEEKLY', 'BIWEEKLY', 'SPECIFIC_WEEKDAYS', 'DAILY'].includes(watch('recurringPattern') || '') && (
                  <Controller name="recurringWeekdays" control={control} render={({ field }) => (
                    <Box sx={{ display: 'flex', gap: 0.25 }}>
                      {weekdayOptions.map(w => {
                        const selected = (field.value || []).includes(w.value);
                        return (
                          <Chip
                            key={w.value}
                            label={w.label}
                            size="small"
                            color={selected ? 'primary' : 'default'}
                            variant={selected ? 'filled' : 'outlined'}
                            onClick={() => {
                              const current = field.value || [];
                              field.onChange(selected ? current.filter(d => d !== w.value) : [...current, w.value].sort());
                            }}
                          />
                        );
                      })}
                    </Box>
                  )} />
                )}
                <Box sx={{ flex: 1 }} />
                <TextField size="small" label="Slutdato" type="date" InputLabelProps={{ shrink: true }} sx={{ width: 145 }}
                  {...register('recurringEndDate')} />
              </Box>
            )}

            {/* SECTION: Kunde */}
            <Box sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
              <SectionLabel>Kunde</SectionLabel>
              <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5, flexWrap: 'wrap' }}>
                <Controller name="customerName" control={control} rules={{ required: 'Påkrævet' }}
                  render={({ field }) => (
                    <Autocomplete size="small" sx={{ flex: 2, minWidth: 220 }}
                      options={customers}
                      getOptionLabel={(opt) => typeof opt === 'string' ? opt : opt.client}
                      isOptionEqualToValue={(opt, val) => {
                        const a = typeof opt === 'string' ? opt : opt.client;
                        const b = typeof val === 'string' ? val : val?.client;
                        return a === b;
                      }}
                      filterOptions={(options, state) => {
                        const q = normalizeSearch(state.inputValue);
                        if (!q) return options.slice(0, 100);
                        const tokens = q.split(/\s+/).filter(Boolean);

                        // Score each option — higher = better match. Name matches outrank
                        // number/email/address matches; prefix matches outrank substring matches.
                        const scored: Array<{ o: typeof options[number]; score: number }> = [];
                        for (const o of options) {
                          if (typeof o === 'string') {
                            const name = normalizeSearch(o);
                            if (tokens.every(t => name.includes(t))) scored.push({ o, score: 100 });
                            continue;
                          }
                          const name = normalizeSearch(o.client);
                          const number = normalizeSearch(o.clientNumber);
                          const other = normalizeSearch(`${o.clientEmail || ''} ${o.clientAddress || ''}`);

                          let score = 0;
                          let allMatched = true;
                          for (const t of tokens) {
                            if (name.startsWith(t)) score += 1000;
                            else if (new RegExp(`\\b${t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`).test(name)) score += 500;
                            else if (name.includes(t)) score += 200;
                            else if (number.startsWith(t)) score += 100;
                            else if (number.includes(t)) score += 50;
                            else if (other.includes(t)) score += 10;
                            else { allMatched = false; break; }
                          }
                          if (allMatched) scored.push({ o, score });
                        }
                        // Sort by score desc, tie-break on name length (shorter name = better match)
                        scored.sort((a, b) => {
                          if (b.score !== a.score) return b.score - a.score;
                          const la = typeof a.o === 'string' ? (a.o as string).length : (a.o as { client: string }).client.length;
                          const lb = typeof b.o === 'string' ? (b.o as string).length : (b.o as { client: string }).client.length;
                          return la - lb;
                        });
                        return scored.slice(0, 100).map(s => s.o);
                      }}
                      renderOption={(props, option) => {
                        if (typeof option === 'string') return <li {...props}>{option}</li>;
                        return (
                          <li {...props} key={option.clientNumber || option.client}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                              <Typography variant="body2" fontWeight={500}>{option.client}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {option.clientNumber}
                                {option.clientEmail ? ` · ${option.clientEmail}` : ''}
                              </Typography>
                            </Box>
                          </li>
                        );
                      }}
                      ListboxProps={{ style: { maxHeight: 360 } }}
                      value={customers.find(c => c.client === field.value) ?? (field.value ? { client: field.value, clientNumber: '' } : null)}
                      onChange={(_, val) => {
                        if (val && typeof val !== 'string') {
                          field.onChange(val.client);
                          setValue('customerNumber', val.clientNumber);
                          setValue('customerPhone', val.clientPhone || '');
                          setValue('customerEmail', val.clientEmail || '');
                        } else {
                          field.onChange('');
                          setValue('customerNumber', '');
                          setValue('customerPhone', '');
                          setValue('customerEmail', '');
                        }
                      }}
                      renderInput={(params) => (
                        <TextField {...params} placeholder="Søg på navn, kundenr, email…" error={!!errors.customerName} />
                      )}
                    />
                  )}
                />
                <TextField size="small" placeholder="Kundenr" sx={{ width: 90 }}
                  InputProps={{ readOnly: true }}
                  {...register('customerNumber')} />
                <TextField size="small" placeholder="Email *" sx={{ flex: 2, minWidth: 180 }}
                  {...register('customerEmail', { required: 'Påkrævet', pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Ugyldig email' } })}
                  error={!!errors.customerEmail}
                  helperText={errors.customerEmail ? undefined : 'Forudfyldt fra DORA — kan overskrives'}
                  FormHelperTextProps={{ sx: { fontSize: 9, mx: 0, mt: 0 } }} />
                <TextField size="small" placeholder="Telefon" sx={{ flex: 1, minWidth: 110 }}
                  InputProps={{ readOnly: true }}
                  {...register('customerPhone')} />
              </Box>
            </Box>

            {/* SECTION: Loads — one per DORA cargo */}
            <Box sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <SectionLabel sx={{ mb: 0 }}>
                  Læs {loadArray.fields.length > 1 && `(${loadArray.fields.length})`}
                  <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1, textTransform: 'none', letterSpacing: 0, fontWeight: 400 }}>
                    Hvert læs bliver en separat ordre i DORA ved konvertering
                  </Typography>
                </SectionLabel>
                <Button size="small" startIcon={<AddIcon fontSize="small" />}
                  onClick={() => loadArray.append(makeEmptyLoad())}
                  sx={{ fontSize: 11, py: 0.25 }}>
                  Tilføj læs
                </Button>
              </Box>

              {loadArray.fields.map((loadField, loadIdx) => {
                const currentLoad = watch(`loads.${loadIdx}`);
                const pickups = currentLoad?.pickups || [];
                const dropoffs = currentLoad?.dropoffs || [];
                const loadAdr = currentLoad?.adr;
                const isMulti = loadArray.fields.length > 1;

                return (
                  <Box key={loadField.id}
                    sx={{
                      mb: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1,
                      bgcolor: isMulti ? '#fafbfc' : 'transparent',
                    }}>
                    {isMulti && (
                      <Box sx={{
                        px: 1, py: 0.5, borderBottom: '1px solid', borderColor: 'divider',
                        display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'primary.50',
                      }}>
                        <Typography variant="caption" fontWeight={700} color="primary.dark">
                          Læs {loadIdx + 1}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {allCargoTypes[currentLoad?.cargoType || ''] || 'Ikke valgt'} · {pickups.length} afh. / {dropoffs.length} afl.
                        </Typography>
                        <Box sx={{ flex: 1 }} />
                        {loadArray.fields.length > 1 && (
                          <IconButton size="small" color="error" onClick={() => loadArray.remove(loadIdx)} sx={{ p: 0.25 }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    )}

                    {/* Transport details for this load */}
                    <Box sx={{ p: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <SectionLabel>Transport</SectionLabel>
                        {!isMulti && (
                          <Button size="small" onClick={() => setShowAdvanced(!showAdvanced)}
                            endIcon={showAdvanced ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                            sx={{ color: 'text.secondary', fontSize: 10, py: 0, minHeight: 0, mb: 0.5 }}>
                            Avanceret
                          </Button>
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        <CargoTypeSelect control={control} loadIdx={loadIdx} />
                        <TextField size="small" placeholder="Beskrivelse" sx={{ flex: 2, minWidth: 140 }}
                          {...register(`loads.${loadIdx}.description`)} />
                        <Controller name={`loads.${loadIdx}.teamId`} control={control} render={({ field }) => (
                          <FormControl size="small" sx={{ minWidth: 100 }}>
                            <Select {...field} displayEmpty>
                              <MenuItem value=""><em>Team</em></MenuItem>
                              {teams.map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
                            </Select>
                          </FormControl>
                        )} />
                        <Controller name={`loads.${loadIdx}.labelId`} control={control} render={({ field }) => (
                          <FormControl size="small" sx={{ minWidth: 100 }}>
                            <Select {...field} displayEmpty>
                              <MenuItem value=""><em>Label</em></MenuItem>
                              {labels.map(l => <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>)}
                            </Select>
                          </FormControl>
                        )} />
                      </Box>
                      <Collapse in={showAdvanced || isMulti}>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                          <TextField size="small" placeholder="Beskrivelse 2" sx={{ flex: 2, minWidth: 130 }}
                            {...register(`loads.${loadIdx}.description2`)} />
                          <Controller name={`loads.${loadIdx}.adr`} control={control} render={({ field }) => (
                            <FormControlLabel sx={{ m: 0 }} control={<Checkbox checked={field.value} onChange={field.onChange} size="small" />} label={<Typography variant="caption">ADR</Typography>} />
                          )} />
                          {loadAdr && <TextField size="small" placeholder="ADR tekst" sx={{ flex: 1, minWidth: 90 }}
                            {...register(`loads.${loadIdx}.adrText`)} />}
                          <TextField size="small" placeholder="Min °C" type="number" sx={{ width: 70 }}
                            {...register(`loads.${loadIdx}.minTemperature`)} />
                          <TextField size="small" placeholder="Max °C" type="number" sx={{ width: 70 }}
                            {...register(`loads.${loadIdx}.maxTemperature`)} />
                          <TextField size="small" placeholder="Note" sx={{ flex: 1, minWidth: 140 }}
                            {...register(`loads.${loadIdx}.note`)} />
                        </Box>
                      </Collapse>
                    </Box>

                    <Divider />

                    {/* Stops for this load */}
                    <Box sx={{ display: 'flex' }}>
                      <Box sx={{ flex: 1, borderRight: '1px solid', borderColor: 'divider', p: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                          <SectionLabel sx={{ mb: 0 }}>Afhentninger</SectionLabel>
                          <Button size="small" startIcon={<AddIcon fontSize="small" />}
                            onClick={() => setValue(`loads.${loadIdx}.pickups`, [...pickups, { ...emptyStop }])}
                            sx={{ fontSize: 10, py: 0, minHeight: 0 }}>
                            Tilføj
                          </Button>
                        </Box>
                        {pickups.map((_, stopIdx) => (
                          <StopRow
                            key={stopIdx}
                            prefix={`loads.${loadIdx}.pickups.${stopIdx}`}
                            index={stopIdx}
                            register={register}
                            setValue={setValue}
                            watch={watch}
                            salesLocations={salesLocations}
                            onRemove={() => setValue(`loads.${loadIdx}.pickups`, pickups.filter((_, i) => i !== stopIdx))}
                            canRemove={pickups.length > 1}
                            kind="pickup"
                          />
                        ))}
                      </Box>
                      <Box sx={{ flex: 1, p: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                          <SectionLabel sx={{ mb: 0 }}>Afleveringer</SectionLabel>
                          <Button size="small" startIcon={<AddIcon fontSize="small" />}
                            onClick={() => setValue(`loads.${loadIdx}.dropoffs`, [...dropoffs, { ...emptyStop }])}
                            sx={{ fontSize: 10, py: 0, minHeight: 0 }}>
                            Tilføj
                          </Button>
                        </Box>
                        {dropoffs.map((_, stopIdx) => (
                          <StopRow
                            key={stopIdx}
                            prefix={`loads.${loadIdx}.dropoffs.${stopIdx}`}
                            index={stopIdx}
                            register={register}
                            setValue={setValue}
                            watch={watch}
                            salesLocations={salesLocations}
                            onRemove={() => setValue(`loads.${loadIdx}.dropoffs`, dropoffs.filter((_, i) => i !== stopIdx))}
                            canRemove={dropoffs.length > 1}
                            kind="dropoff"
                          />
                        ))}
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>

            {/* SECTION: Prislinjer */}
            <Box sx={{ p: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <SectionLabel sx={{ mb: 0 }}>Prislinjer</SectionLabel>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Button size="small" onClick={() => setCatalogDialogOpen(true)} sx={{ fontSize: 10, py: 0, minHeight: 0 }}>
                    Fra katalog
                  </Button>
                  <Button size="small" startIcon={<AddIcon fontSize="small" />} onClick={() => priceLineArray.append({ ...emptyPriceLine })} sx={{ fontSize: 10, py: 0, minHeight: 0 }}>
                    Tilføj
                  </Button>
                </Box>
              </Box>
              {priceLineArray.fields.map((field, i) => (
                <Box key={field.id} sx={{ display: 'flex', gap: 0.5, mb: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Controller
                    name={`priceLines.${i}.productId`}
                    control={control}
                    render={({ field: prodField }) => (
                      <Autocomplete
                        size="small"
                        sx={{ width: 190 }}
                        options={products}
                        getOptionLabel={(o) => `${o.productNumber} — ${o.name}`}
                        value={products.find(p => p.id === prodField.value) || null}
                        onChange={async (_e, val) => {
                          prodField.onChange(val?.id || '');
                          setValue(`priceLines.${i}.productNumber`, val?.productNumber || '');
                          setValue(`priceLines.${i}.description`, val?.name || '');
                          // Default fra DORA — overskrives nedenfor af resolveren hvis kunde+lokation matcher
                          setValue(`priceLines.${i}.amount`, val?.salesPrice ? parseFloat(val.salesPrice) : 0);
                          if (!watch(`priceLines.${i}.unitName`)) {
                            setValue(`priceLines.${i}.unitName`, 'stk');
                          }
                          // Resolve effektiv pris: kunde-pris > lokations-pris > DORA-default.
                          // Find første pickup på det tilknyttede læs der har et salgssted.
                          if (val) {
                            const currentLoadId = watch(`priceLines.${i}.loadId`) || '';
                            const customerNumber = watch('customerNumber') || null;
                            const loadsData = watch('loads') || [];
                            const matchingLoads = currentLoadId ? loadsData.filter(l => l.id === currentLoadId) : loadsData;
                            let pickupSalesLocId: string | null = null;
                            for (const load of matchingLoads) {
                              const p = load.pickups.find(p => p.salesLocationId);
                              if (p?.salesLocationId) { pickupSalesLocId = p.salesLocationId; break; }
                            }
                            if (customerNumber || pickupSalesLocId) {
                              try {
                                const resolved = await api.resolveProductPrice({
                                  doraProductId: val.id,
                                  customerNumber,
                                  salesLocationId: pickupSalesLocId,
                                });
                                if (resolved.price != null) {
                                  setValue(`priceLines.${i}.amount`, resolved.price);
                                  if (resolved.unitName) setValue(`priceLines.${i}.unitName`, resolved.unitName);
                                  if (resolved.source === 'CUSTOMER') {
                                    setSnackbar({ open: true, message: `Brugte kundepris (${customerNumber}) for ${val.name}`, severity: 'success' });
                                  } else if (resolved.source === 'LOCATION') {
                                    setSnackbar({ open: true, message: `Brugte ${resolved.salesLocationName}-pris for ${val.name}`, severity: 'success' });
                                  }
                                }
                              } catch {
                                // Resolver-fejl er ikke kritisk — DORA-default er allerede sat
                              }
                            }
                          }
                          // Auto-trigger: hvis dette produkt har en trigger på tilknyttet salgssted,
                          // tilføj automatisk en ekstra prislinje (fx råstofafgift).
                          if (val) {
                            const currentLoadId = watch(`priceLines.${i}.loadId`) || '';
                            const trigger = findAutoTriggerForProduct(val.id, currentLoadId);
                            if (trigger) {
                              const existingLines = watch('priceLines') || [];
                              const alreadyExists = existingLines.some((l, idx) =>
                                idx !== i
                                && l.productId === trigger.triggerProductId
                                && (l.loadId || '') === trigger.sourceLoadId
                              );
                              if (!alreadyExists) {
                                const sourceQty = watch(`priceLines.${i}.quantity`) || 1;
                                priceLineArray.append({
                                  description: trigger.triggerProductName,
                                  amount: trigger.price,
                                  quantity: sourceQty * trigger.factor,
                                  unitName: trigger.unitName,
                                  productId: trigger.triggerProductId,
                                  productNumber: trigger.triggerProductNumber,
                                  discountPercentage: '',
                                  loadId: trigger.sourceLoadId,
                                });
                                setSnackbar({ open: true, message: `Auto-tilføjet: ${trigger.triggerProductName} (fra ${trigger.locationName})`, severity: 'success' });
                              }
                            }
                          }
                        }}
                        renderInput={(params) => <TextField {...params} placeholder="Vælg produkt fra DORA *" />}
                        isOptionEqualToValue={(o, v) => o.id === v.id}
                      />
                    )}
                  />
                  <TextField size="small" placeholder="Produktnr" sx={{ width: 70 }}
                    InputProps={{ readOnly: true }}
                    {...register(`priceLines.${i}.productNumber`)} />
                  <TextField size="small" placeholder="Beskrivelse" sx={{ flex: 2, minWidth: 120 }} {...register(`priceLines.${i}.description`)} />
                  <TextField size="small" placeholder="Antal" type="number" sx={{ width: 60 }} {...register(`priceLines.${i}.quantity`, { valueAsNumber: true })} />
                  <TextField size="small" placeholder="Enhed" sx={{ width: 60 }} {...register(`priceLines.${i}.unitName`)} />
                  <TextField size="small" placeholder="Beløb" type="number" sx={{ width: 85 }} {...register(`priceLines.${i}.amount`, { valueAsNumber: true })} />
                  <TextField size="small" placeholder="Rabat%" sx={{ width: 60 }} {...register(`priceLines.${i}.discountPercentage`)} />
                  {loadArray.fields.length > 1 && (
                    <Controller name={`priceLines.${i}.loadId`} control={control} render={({ field }) => (
                      <FormControl size="small" sx={{ minWidth: 110 }}>
                        <Select {...field} displayEmpty>
                          <MenuItem value=""><em>Hele tilbuddet</em></MenuItem>
                          {loadArray.fields.map((lf, li) => {
                            const load = watch(`loads.${li}`);
                            const label = `Læs ${li + 1}${load?.cargoType ? ` · ${allCargoTypes[load.cargoType] || load.cargoType}` : ''}`;
                            return <MenuItem key={lf.id} value={load?.id || ''}>{label}</MenuItem>;
                          })}
                        </Select>
                      </FormControl>
                    )} />
                  )}
                  {priceLineArray.fields.length > 1 && (
                    <IconButton size="small" color="error" onClick={() => priceLineArray.remove(i)} sx={{ p: 0.25 }}><DeleteIcon fontSize="small" /></IconButton>
                  )}
                </Box>
              ))}
            </Box>

            {/* SECTION: Vilkår */}
            <Box sx={{ p: 1, borderTop: '1px solid', borderColor: 'divider', display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
              <SectionLabel sx={{ mb: 0, mr: 0.5 }}>Vilkår</SectionLabel>
              <Controller name="currency" control={control} render={({ field }) => (
                <FormControl size="small" sx={{ minWidth: 70 }}>
                  <Select {...field} displayEmpty>
                    <MenuItem value="DKK">DKK</MenuItem>
                    <MenuItem value="EUR">EUR</MenuItem>
                    <MenuItem value="SEK">SEK</MenuItem>
                    <MenuItem value="NOK">NOK</MenuItem>
                  </Select>
                </FormControl>
              )} />
              <TextField size="small" label="Gyldig til" type="date" InputLabelProps={{ shrink: true }} sx={{ width: 140 }} {...register('validUntil')} />
              <TextField size="small" placeholder="Betalingsbetingelser, leveringsvilkår osv." sx={{ flex: 1, minWidth: 170 }} {...register('terms')} />
            </Box>
          </CardContent>
        </Card>

        {/* Intern note — kun internt */}
        <Card sx={{ mb: 1.5 }}>
          <CardContent sx={{ p: 1 }}>
            <SectionLabel>Intern note (vises ikke til kunde)</SectionLabel>
            <TextField size="small" fullWidth multiline minRows={2} placeholder="Interne kommentarer, fx særlige hensyn, prisnoter, noter til sælger..."
              {...register('internalNote')} />
          </CardContent>
        </Card>

        {/* DG-input strip — meget kompakt; resultat i sticky footer */}
        <Box sx={{
          mb: 1.5, px: 1, py: 0.75, borderRadius: 1, border: '1px dashed', borderColor: 'divider',
          display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap',
        }}>
          <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mr: 0.5 }}>
            DG-input:
          </Typography>
          <Controller name="estimateTrailerId" control={control} render={({ field }) => (
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <Select {...field} displayEmpty>
                <MenuItem value=""><em>Vælg trailer</em></MenuItem>
                {trailers.map(t => (
                  <MenuItem key={t.id} value={t.id}>{t.number}{t.alias ? ` — ${t.alias}` : ''}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )} />
          <TextField size="small" placeholder="km" type="number" sx={{ width: 80 }}
            {...register('estimateKm')} />
          <TextField size="small" placeholder="timer" type="number" sx={{ width: 80 }}
            {...register('estimateHours')} />
          <TextField size="small" placeholder="dage" type="number" sx={{ width: 70 }}
            {...register('estimateCalendarDays')} />
          <Tooltip title="DG og omkostning vises i den nederste linje">
            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', fontStyle: 'italic' }}>
              ↓ vises nederst
            </Typography>
          </Tooltip>
        </Box>

        {/* Reminder block — internal email reminders to the user who created the quote */}
        <Box sx={{ mt: 1.5 }}>
          <SectionLabel>Påmindelser til mig</SectionLabel>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Få email-påmindelse hvis tilbuddet stadig ikke er accepteret eller konverteret efter X timer.
            Tilbud sendt: tæller starter ved afsendelse.
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Controller name="reminderEnabled" control={control} render={({ field }) => (
              <FormControlLabel
                sx={{ m: 0 }}
                control={<Checkbox size="small" checked={!!field.value} onChange={e => field.onChange(e.target.checked)} />}
                label={<Typography variant="body2">Aktivér påmindelser</Typography>}
              />
            )} />
            {watch('reminderEnabled') && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <TextField
                  size="small" type="number" sx={{ width: 70 }}
                  {...register('reminderHour1')}
                  inputProps={{ min: 0, step: 1 }}
                />
                <TextField
                  size="small" type="number" sx={{ width: 70 }}
                  {...register('reminderHour2')}
                  inputProps={{ min: 0, step: 1 }}
                />
                <TextField
                  size="small" type="number" sx={{ width: 70 }}
                  {...register('reminderHour3')}
                  inputProps={{ min: 0, step: 1 }}
                />
                <Typography variant="caption" color="text.secondary">
                  timer efter afsendelse (sæt til 0 for at springe over)
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
        </Box>
        {/* --- /INTERN TAB --- */}

        {/* --- KUNDE TAB --- */}
        <Box sx={{ display: activeTab === 1 ? 'block' : 'none' }}>
          <Card sx={{ mb: 1.5 }}>
            <CardContent sx={{ p: 2 }}>
              <SectionLabel>Besked til kunde</SectionLabel>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Vises øverst på kundens PDF og accepterings-link. Brug den til en personlig introduktion eller projektkontekst.
              </Typography>
              <TextField size="small" fullWidth multiline minRows={3}
                placeholder="Kære [kunde], tak for din forespørgsel. Vi er glade for at kunne tilbyde følgende..."
                {...register('customerMessage')} />

              <Divider sx={{ my: 2 }} />

              <SectionLabel>
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                  <VisibilityOffIcon sx={{ fontSize: 12 }} /> Skjul for kunde
                </Box>
              </SectionLabel>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Markér sektioner der <strong>ikke</strong> skal vises til kunden. Dataene bevares internt; kun PDF'en og accepter-linket skjuler dem.
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Controller name="hidePickupsFromCustomer" control={control} render={({ field }) => (
                  <FormControlLabel
                    control={<Checkbox checked={field.value} onChange={field.onChange} size="small" />}
                    label={<Typography variant="body2">Skjul afhentningsadresser</Typography>}
                  />
                )} />
                <Controller name="hideDropoffsFromCustomer" control={control} render={({ field }) => (
                  <FormControlLabel
                    control={<Checkbox checked={field.value} onChange={field.onChange} size="small" />}
                    label={<Typography variant="body2">Skjul leveringsadresser</Typography>}
                  />
                )} />
                <Controller name="hideCustomerNumberOnPdf" control={control} render={({ field }) => (
                  <FormControlLabel
                    control={<Checkbox checked={field.value} onChange={field.onChange} size="small" />}
                    label={<Typography variant="body2">Skjul kundenummer på PDF</Typography>}
                  />
                )} />
              </Box>
            </CardContent>
          </Card>

          {/* Preview-kort: hvad kunden ser */}
          <Card sx={{ mb: 1.5, bgcolor: '#fafafa' }}>
            <CardContent sx={{ p: 2 }}>
              <SectionLabel>Preview — sådan ser kunden tilbuddet</SectionLabel>
              <Box sx={{ mt: 1, p: 2, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                {watch('customerMessage') && (
                  <Typography variant="body2" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
                    {watch('customerMessage')}
                  </Typography>
                )}
                <Typography variant="subtitle2" fontWeight={600}>Kunde</Typography>
                <Typography variant="body2">{watch('customerName') || '—'}</Typography>
                <Typography variant="body2" color="text.secondary">{watch('customerEmail') || '—'}</Typography>
                {watch('customerPhone') && <Typography variant="body2" color="text.secondary">Tlf: {watch('customerPhone')}</Typography>}
                {watch('customerNumber') && !watch('hideCustomerNumberOnPdf') && (
                  <Typography variant="body2" color="text.secondary">Kundenr: {watch('customerNumber')}</Typography>
                )}

                <Divider sx={{ my: 1.5 }} />

                {watch('loads').map((load, i) => {
                  const multi = watch('loads').length > 1;
                  const loadLabel = multi ? `Læs ${i + 1}` : 'Transport';
                  return (
                    <Box key={i} sx={{ mb: 1.5 }}>
                      <Typography variant="subtitle2" fontWeight={600}>{loadLabel}</Typography>
                      {load.cargoType && (
                        <Typography variant="body2" color="text.secondary">
                          Godstype: {allCargoTypes[load.cargoType] || load.cargoType}
                        </Typography>
                      )}
                      {load.description && (
                        <Typography variant="body2" color="text.secondary">{load.description}</Typography>
                      )}

                      {!watch('hidePickupsFromCustomer') && load.pickups.length > 0 && (
                        <>
                          <Typography variant="caption" fontWeight={600} sx={{ display: 'block', mt: 0.5 }}>Afhentninger</Typography>
                          {load.pickups.map((p, pi) => (
                            <Typography key={pi} variant="body2" color="text.secondary">
                              {pi + 1}. {[p.placeName, p.address, p.postcode, p.city].filter(Boolean).join(', ') || '—'}
                              {p.date && ` — ${p.date}`}
                            </Typography>
                          ))}
                        </>
                      )}
                      {!watch('hideDropoffsFromCustomer') && load.dropoffs.length > 0 && (
                        <>
                          <Typography variant="caption" fontWeight={600} sx={{ display: 'block', mt: 0.5 }}>Afleveringer</Typography>
                          {load.dropoffs.map((d, di) => (
                            <Typography key={di} variant="body2" color="text.secondary">
                              {di + 1}. {[d.placeName, d.address, d.postcode, d.city].filter(Boolean).join(', ') || '—'}
                              {d.date && ` — ${d.date}`}
                            </Typography>
                          ))}
                        </>
                      )}
                    </Box>
                  );
                })}
                {watch('hidePickupsFromCustomer') && (
                  <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>Afhentninger skjult</Typography>
                )}
                {watch('hideDropoffsFromCustomer') && (
                  <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>Afleveringer skjult</Typography>
                )}

                <Divider sx={{ my: 1.5 }} />

                <Typography variant="subtitle2" fontWeight={600}>Prisspecifikation</Typography>
                {watchPriceLines.filter(l => l.description).map((l, i) => (
                  <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">{l.description} ({l.quantity} {l.unitName || 'stk'})</Typography>
                    <Typography variant="body2">
                      {new Intl.NumberFormat('da-DK', { style: 'currency', currency: watch('currency') || 'DKK' }).format((l.amount || 0) * (l.quantity || 1))}
                    </Typography>
                  </Box>
                ))}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="subtitle2" fontWeight={700}>Total</Typography>
                  <Typography variant="subtitle2" fontWeight={700} color="primary">
                    {new Intl.NumberFormat('da-DK', { style: 'currency', currency: watch('currency') || 'DKK' }).format(totalAmount)}
                  </Typography>
                </Box>

                {watch('terms') && (
                  <>
                    <Divider sx={{ my: 1.5 }} />
                    <Typography variant="subtitle2" fontWeight={600}>Vilkår</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>{watch('terms')}</Typography>
                  </>
                )}
                {/* Per-load notes are rendered inside each load section above. */}
              </Box>
            </CardContent>
          </Card>
        </Box>
        {/* --- /KUNDE TAB --- */}
      </form>

      {/* Sticky bottom action bar with live total + DG */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: { xs: 0, md: 240 }, // sidebar width
          right: 0,
          bgcolor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 -2px 8px rgba(0,0,0,0.06)',
          px: 2, py: 1.25,
          zIndex: 1100,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        {/* Total + DG block */}
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1 }}>Total</Typography>
            <Typography variant="h6" fontWeight={700} color="primary" sx={{ lineHeight: 1.2 }}>
              {new Intl.NumberFormat('da-DK', { style: 'currency', currency: watch('currency') || 'DKK', minimumFractionDigits: 0 }).format(totalAmount)}
            </Typography>
          </Box>
          {marginEstimate ? (
            <>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1 }}>Omkostning</Typography>
                <Typography variant="body1" fontWeight={600} sx={{ lineHeight: 1.2 }}>
                  {new Intl.NumberFormat('da-DK', { style: 'currency', currency: 'DKK', minimumFractionDigits: 0 }).format(marginEstimate.totalCost)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1 }}>DG</Typography>
                <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
                  <Typography variant="body1" fontWeight={600} sx={{ lineHeight: 1.2 }}>
                    {new Intl.NumberFormat('da-DK', { style: 'currency', currency: 'DKK', minimumFractionDigits: 0 }).format(marginEstimate.dg)}
                  </Typography>
                  <Box sx={{
                    px: 0.75, py: 0.1, borderRadius: 0.75, fontWeight: 700, fontSize: 12,
                    bgcolor: `${dgColor(marginEstimate.dgPct)}.main`, color: 'white',
                  }}>
                    {marginEstimate.dgPct.toFixed(0)}%
                  </Box>
                </Box>
              </Box>
              <Tooltip title={`Beregnet med ${marginEstimate.kilometers} km · ${marginEstimate.hours} t · ${marginEstimate.source === 'auto' ? 'auto' : marginEstimate.source === 'mixed' ? 'delvis auto' : 'manuelt'}`}>
                <Typography variant="caption" color="text.secondary" sx={{ cursor: 'help' }}>
                  ⓘ
                </Typography>
              </Tooltip>
            </>
          ) : (
            <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
              Vælg trailer + km/timer for DG
            </Typography>
          )}
          {marginLoading && <CircularProgress size={14} />}
        </Box>

        <Box sx={{ flex: 1 }} />

        {/* Action buttons — bemærk: intet sendes til kunde herfra.
             Send sker eksplicit fra tilbudslisten eller detail-siden. */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={() => navigate(effectiveId() ? `/tilbud/${effectiveId()}` : '/tilbud')} disabled={isPending} size="medium">
            Luk
          </Button>
          <Button
            variant="contained"
            startIcon={isPending ? <CircularProgress size={16} /> : <SaveIcon />}
            onClick={handleSubmit(onSave)}
            disabled={isPending}
            size="medium"
          >
            Gem & luk
          </Button>
        </Box>
      </Box>

      {/* Save-as-template dialog (replaces window.prompt) */}
      <Dialog open={saveTemplateDialog.open} onClose={() => setSaveTemplateDialog(s => ({ ...s, open: false }))} fullWidth maxWidth="xs">
        <DialogTitle>Gem som skabelon</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Navn på skabelon"
            value={saveTemplateDialog.name}
            onChange={e => setSaveTemplateDialog(s => ({ ...s, name: e.target.value }))}
            sx={{ mt: 1 }}
            onKeyDown={e => { if (e.key === 'Enter') confirmSaveTemplate(); }}
          />
          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Checkbox
                checked={saveTemplateDialog.bindToCustomer}
                onChange={e => setSaveTemplateDialog(s => ({ ...s, bindToCustomer: e.target.checked }))}
                disabled={!watch('customerNumber')}
                size="small"
              />
            }
            label={
              <Typography variant="body2">
                Bind til denne kunde {!watch('customerNumber') && <Typography variant="caption" color="text.secondary">(ingen kunde valgt)</Typography>}
              </Typography>
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveTemplateDialog(s => ({ ...s, open: false }))}>Annuller</Button>
          <Button variant="contained" onClick={confirmSaveTemplate} disabled={createTemplate.isPending}>
            {createTemplate.isPending ? <CircularProgress size={18} /> : 'Gem'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        sx={{ bottom: { xs: 90, md: 90 } }}
      >
        <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
